import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Button } from '@ui/button.tsx';
import { zodResolver } from '@hookform/resolvers/zod';
import { useApiAdminapitokensPost } from '@r/api/generated/client.ts';
import { toast } from 'sonner';
import type { FC } from 'react';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

interface ApiTokenFormProps {
  onSuccess: (token: string, tokenName: string) => void;
}

export const ApiTokenForm: FC<ApiTokenFormProps> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();

  const FormSchema = z.object({
    tokenName: z
      .string()
      .min(3, t('form.error.char.min', { minChar: 3 }))
      .max(100, t('form.error.char.max', { maxChar: 100 }))
      .describe(t('admin.api.token.generation.schema.describe.token.name'))
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      tokenName: ''
    }
  });

  const tokenMutation = useApiAdminapitokensPost({
    mutation: {
      onSuccess: (result, variables) => {
        toast.success(t('admin.api.token.generation.success'));
        onSuccess(result.token, variables.data.name);
      },
      onError: (error) => {
        if (isOrvalErrorFromPath(error, 'post', '/admin/api/tokens')) {
          showOrvalErrorToast(error, t('admin.api.token.generation.error'));
        } else {
          toast.error(t('admin.api.token.generation.error'));
        }
      },
      networkMode: 'always'
    }
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    const normalizedName = data.tokenName.replace(/\s+/g, ' ').trim();
    tokenMutation.mutate({ data: { name: normalizedName } });
  }

  return (
    <Form {...form} schema={FormSchema}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="tokenName"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('admin.api.token.generation.form.input.label')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('admin.api.token.generation.form.input.placeholder')}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          rounded="full"
          loading={tokenMutation.isPending}
          className="mt-5 w-full"
          type="submit"
        >
          {t('admin.api.token.generation.form.button')}
        </Button>
      </form>
    </Form>
  );
};
